import { DetailData, EducationResponse, SkillData, useApiClient } from '@/lib/api';
import { monthInputToApiDate } from '@/lib/profile-dates';
import React, { Dispatch, SetStateAction, useState } from 'react';
import EducationView from './EducationView';

/**
 * EducationList supports both controlled and uncontrolled usage.
 * - If you provide `educations`/`setEducations`, the parent manages state (controlled).
 * - If you omit them, EducationList manages its own state (uncontrolled).
 *
 * This pattern is used by most professional React UI libraries for flexibility.
 */
type StateSetter<T> = Dispatch<SetStateAction<T>>;

interface EducationListProps {
  educations?: EducationResponse[];
  setEducations?: StateSetter<EducationResponse[]>;
  details?: { [educationId: string]: DetailData[] };
  setDetails?: StateSetter<{ [educationId: string]: DetailData[] }>;
  skills?: { [educationId: string]: SkillData[] };
  loading?: boolean;
  setLoading?: StateSetter<boolean>;
}

const EducationList: React.FC<EducationListProps> = ({
  educations,
  setEducations,
  details,
  setDetails,
  skills,
  loading,
  setLoading,
}) => {

  const { api } = useApiClient();

  const [internalEducationList, setInternalEducationList] = useState<EducationResponse[]>([]);
  const [internalDetails, setInternalDetails] = useState<{ [educationId: string]: DetailData[] }>({});
  const [internalSkills] = useState<{ [educationId: string]: SkillData[] }>({});
  const [internalIsLoading, internalSetIsLoading] = useState(false);

  const educationList = educations !== undefined ? educations : internalEducationList;
  const educationDetails = details ?? internalDetails;
  const educationSkills = skills ?? internalSkills;
  const isLoading = loading !== undefined ? loading : internalIsLoading;

  const setEducationList = setEducations !== undefined ? setEducations : setInternalEducationList;
  const setEducationDetails = setDetails !== undefined ? setDetails : setInternalDetails;

  const onDeleteEducation = async (id?: string) => {
    if (!id) return;
    const setLoadingState = setLoading || (() => {});
    setLoadingState(true);
    try {
      await api.deleteEducation(id);
      setEducationList(list => list.filter(e => e.id !== id));
    } catch (err) {
      console.error('[Education] Failed to delete education', err);
    } finally {
      setLoadingState(false);
    }
  };

  const onUpdateEducation = async (education: EducationResponse) => {
    if (!education.id) return;
    const setLoadingState = setLoading || (() => {});
    setLoadingState(true);
    try {
      const updatedEducation = await api.updateEducation(education.id, {
        institution: education.institution,
        degree_type: education.degree_type,
        major: education.major,
        start_date: monthInputToApiDate(education.start_date),
        end_date: education.is_ongoing ? null : monthInputToApiDate(education.end_date) || null,
        is_ongoing: Boolean(education.is_ongoing),
      });
      setEducationList(list => list.map(item => item.id === education.id ? updatedEducation : item));
    } catch (err) {
      console.error('[Education] Failed to update education', err);
    } finally {
      setLoadingState(false);
    }
  };

  if (!educationList || educationList.length === 0) {
    return <p className="text-sm text-gray-500 italic">No education entries yet.</p>;
  }

  const sortedList = [...educationList].sort((a, b) => {
    if (a.is_ongoing && !b.is_ongoing) return -1;
    if (!a.is_ongoing && b.is_ongoing) return 1;
    if (a.end_date && b.end_date) {
      return new Date(b.end_date).getTime() - new Date(a.end_date).getTime();
    }
    return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      {sortedList.map((edu, idx) => (
        <EducationView
          key={edu.id || idx}
          education={edu}
          details={educationDetails[edu.id!] ?? []}
          skills={educationSkills[edu.id!] ?? []}
          loading={isLoading}
          onDelete={() => onDeleteEducation(edu.id)}
          onUpdate={onUpdateEducation}
          onDetailsChange={(nextDetails) => {
            if (!edu.id) return;
            setEducationDetails(prev => ({ ...prev, [edu.id!]: nextDetails }));
          }}
        />
      ))}
    </div>
  );
};

export default EducationList;
